import React, { memo, useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "./NewPaymentForm.scss";
import {
  initiatePayment,
  initiateAlfalahPayment,
} from "../../../utils/subscriptions";

const NewPaymentForm = ({
  subscriptionData,
  classroomId,
  formikRef,
  userInformation,
  initialValues,
  setLoading,
  selectedPricingOptions,
  setSelectedPricingOptions,
  selectedBank,
}) => {
  const [currencyRate, setCurrencyRate] = useState({});
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        addressLine1: Yup.string().required("Address is required"),
        city: Yup.string().required("City is required"),
        state: Yup.string().required("State is required"),
        country: Yup.string().required("Country is required"),
      }),
    []
  );

  const formatOptionLabel = (option) => {
    switch (option.pricingModel) {
      case "Hourly Plan":
        return `${option.hours} Hours at ${
          option.currency || subscriptionData[0]?.currency
        } ${option.perHourPrice}/hour`;
      case "Free Plan":
        return "Free";
      default:
        const days = option.validity || 30;
        const months = Math.floor(days / 30);
        const remainingDays = days % 30;
        let label = "";
        if (months > 0) label += `${months} month${months > 1 ? "s" : ""} `;
        if (remainingDays > 0)
          label += `${remainingDays} day${remainingDays > 1 ? "s" : ""}`;
        return `${label.trim()} - ${
          option.currency || subscriptionData[0]?.currency
        } ${
          option.finalPrice || option.price * (1 - (option.discount || 0) / 100)
        }`;
    }
  };

  const formatPriceForPayment = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);

    const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
    const normalized = rounded
      .toFixed(2)
      .replace(/\.0+$/, "")
      .replace(/(\.\d)0$/, "$1");

    return normalized.includes(".") ? normalized : `${normalized}.0`;
  };

  const fetchRate = async () => {
    try {
      const response = await fetch(
        "https://api.exchangerate-api.com/v4/latest/PKR"
      );
      const data = await response.json();
      setCurrencyRate(data.rates);
    } catch (err) {
      console.error("Error fetching rate:", err);
    }
  };

  useEffect(() => {
    fetchRate();
  }, []);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    const finalPrice = subscriptionData.reduce((acc, subscription) => {
      const option =
        selectedPricingOptions[subscription._id] ||
        subscription.applicableOptions[0];
      if (!option) return acc;
      if (option.pricingModel === "Hourly Plan") {
        const pricePerHour = parseFloat(option.perHourPrice || 0);
        const hours = parseFloat(option.hours || 0);
        const discount = parseFloat(option.discount || 0);
        return acc + pricePerHour * hours * (1 - discount / 100);
      }
      return (
        acc +
        (option.finalPrice || option.price * (1 - (option.discount || 0) / 100))
      );
    }, 0);

    // Get the primary pricing details for flattening
    const primarySubscription = subscriptionData[0];
    const primaryPricingOption =
      selectedPricingOptions[primarySubscription?._id] ||
      primarySubscription?.applicableOptions[0];

    const formData = {
      forename: userInformation.fullName.split(" ")[0],
      surname: userInformation.fullName.split(" ").slice(1).join(" "),
      email: userInformation.email,
      phone: String(userInformation.phone), // Ensure string
      addressLine: values.addressLine1,
      city: values.city,
      state: values.state,
      country: values.country,
      postalCode: "54000", // Default; adjust as needed
      currency: subscriptionData[0]?.currency.toUpperCase() || "USD",
      finalPrice:
        selectedBank === "ALFA"
          ? Math.round(finalPrice).toString()
          : formatPriceForPayment(finalPrice),
      classroomName: "SAT class", // Update if dynamic
      subscriptionName: subscriptionData.map((sub) => sub.name).join(", "),
      classroomId: String(classroomId), // Ensure string
      subscriptionId: subscriptionData.map((sub) => sub._id).join(", "),
      // Flattened pricing details
      ...(primaryPricingOption && {
        pricingModel: primaryPricingOption.pricingModel,
        ...(primaryPricingOption.pricingModel === "Hourly Plan"
          ? {
              // Hourly Plan specific fields
              hours: String(primaryPricingOption.hours), // Ensure string
              perHourPrice: String(primaryPricingOption.perHourPrice), // Ensure string
              discount: String(primaryPricingOption.discount || 0), // Ensure string
            }
          : {
              // Non-Hourly Plan fields
              price: String(primaryPricingOption.price), // Ensure string
              discount: String(primaryPricingOption.discount || 0), // Ensure string
              validity: String(primaryPricingOption.validity), // Ensure string
              pricingFinalPrice: String(
                primaryPricingOption.finalPrice || primaryPricingOption.price
              ), // Ensure string
            }),
        pricingCurrency: primaryPricingOption.currency,
        showSelected: primaryPricingOption.showSelected || false,
      }),
    };

    if (formData?.currency !== "PKR") {
      const currRate = currencyRate[formData?.currency];
      if (finalPrice && currRate) {
        formData.amountInRs = (finalPrice / currRate).toFixed(2);
      }
    }

    try {
      localStorage.setItem("selectedSubscription", subscriptionData[0]?._id);
      localStorage.setItem("paymentFormData", JSON.stringify(values));

      if (selectedBank === "ALFA") {
        // Bank Alfalah payment flow
        const response = await initiateAlfalahPayment(
          JSON.stringify(formData),
          {
            toastText: "Redirecting to Bank Alfalah Payment Page",
          }
        );

        // Check if response contains data
        if (response) {
          // Create a form dynamically and submit it to Bank Alfalah SSO
          const form = document.createElement("form");
          form.method = "POST";
          form.action = "https://payments.bankalfalah.com/SSO/SSO/SSO";

          // Add all response fields as hidden inputs (except certain fields)
          const excludeFields = [
            "address",
            "paymentMethod",
            "handShakeStatus",
            "walletId",
            "useWallet",
          ];

          const orderedKeys = [
            "AuthToken",
            "RequestHash",
            "ChannelId",
            "Currency",
            "IsBIN",
            "ReturnURL",
            "MerchantId",
            "StoreId",
            "MerchantHash",
            "MerchantUsername",
            "MerchantPassword",
            "TransactionTypeId",
            "TransactionReferenceNumber",
            "TransactionAmount",
          ];

          const addHiddenInput = (key, value) => {
            if (excludeFields.includes(key)) {
              return;
            }
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = value;
            form.appendChild(input);
          };

          // Add required fields in the exact order expected by Alfalah
          orderedKeys.forEach((key) => {
            if (key === "TransactionTypeId") {
              addHiddenInput(key, response?.[key] ?? "3");
              return;
            }
            if (key === "IsBIN") {
              addHiddenInput(key, response?.[key] ?? "0");
              return;
            }
            if (Object.prototype.hasOwnProperty.call(response, key)) {
              addHiddenInput(key, response[key]);
            }
          });

          // Add any remaining fields afterwards (preserve existing behavior)
          for (const key in response) {
            if (orderedKeys.includes(key)) continue;
            addHiddenInput(key, response[key]);
          }

          document.body.appendChild(form);
          form.submit();
          form.remove();

          setLoading(false);
        } else {
          throw new Error(
            "Alfalah payment initiation failed: No response data received."
          );
        }
      } else {
        // HBL payment flow (existing)
        const response = await initiatePayment(JSON.stringify(formData), {
          toastText: "Redirected to Payment Page",
        });

        const { sessionUrl } = response;
        if (sessionUrl) {
          setLoading(false);
          window.location.href = sessionUrl;
        } else {
          throw new Error(
            "Payment initiation failed: session URL is undefined."
          );
        }
      }
    } catch (error) {
      setLoading(false);
      console.error("Payment initiation failed:", error);
      localStorage.setItem("paymentFormData", JSON.stringify(values));
      setErrors({ submit: "Payment initiation failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePricingOptionChange = (subscriptionId, event) => {
    const newIndex = parseInt(event.target.value, 10);
    const subscription = subscriptionData.find(
      (sub) => sub._id === subscriptionId
    );
    if (subscription && subscription.applicableOptions) {
      const newSelectedOption = subscription.applicableOptions[newIndex];
      setSelectedPricingOptions((prev) => ({
        ...prev,
        [subscriptionId]: newSelectedOption,
      }));
    }
  };

  return (
    <Formik
      initialValues={
        initialValues || {
          addressLine1: "",
          city: "",
          state: "",
          country: "",
        }
      }
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {(formik) => {
        formikRef.current = formik;
        const { handleSubmit, errors, touched, values } = formik;
        return (
          <Form
            noValidate
            onSubmit={handleSubmit}
            className="new-payment-form mt-3"
          >
            {/* Form Header */}
            <div className="form-section-header mb-3">
              <i className="fas fa-file-invoice"></i>
              <h6>Billing Information</h6>
            </div>

            {subscriptionData.map((subscription) => {
              if (
                !subscription.applicableOptions ||
                subscription.applicableOptions.length === 0
              )
                return null;

              const selectedOption =
                selectedPricingOptions[subscription._id] ||
                subscription.applicableOptions[0];
              const selectedIndex = subscription.applicableOptions.findIndex(
                (opt) => opt === selectedOption
              );

              // Don't show pricing option selector for Hourly Plans
              // as the student has already selected custom hours on the pricing page
              if (selectedOption?.pricingModel === "Hourly Plan") {
                return null;
              }

              return (
                <Form.Group
                  key={subscription._id}
                  controlId={`pricingOption-${subscription._id}`}
                  className="mb-3"
                >
                  <Form.Label>
                    <i
                      className="fas fa-tags me-2"
                      style={{ color: "#667eea" }}
                    ></i>
                    {subscription.name} Pricing Option
                  </Form.Label>
                  <Form.Select
                    value={selectedIndex}
                    onChange={(e) =>
                      handlePricingOptionChange(subscription._id, e)
                    }
                  >
                    {subscription.applicableOptions.map((option, index) => (
                      <option key={index} value={index}>
                        {formatOptionLabel(option)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              );
            })}

            <Form.Group controlId="formAddressLine1" className="mb-3">
              <Form.Label>
                <i
                  className="fas fa-map-marker-alt me-2"
                  style={{ color: "#667eea" }}
                ></i>
                Address*
              </Form.Label>
              <Field
                name="addressLine1"
                type="text"
                className={`form-control ${
                  touched.addressLine1 && errors.addressLine1
                    ? "is-invalid"
                    : ""
                }`}
                placeholder="Address"
                value={values.addressLine1}
              />
              <ErrorMessage
                name="addressLine1"
                component="div"
                className="invalid-feedback"
              />
            </Form.Group>

            <Form.Group controlId="formCity" className="mb-3">
              <Form.Label>
                <i
                  className="fas fa-city me-2"
                  style={{ color: "#667eea" }}
                ></i>
                City*
              </Form.Label>
              <Field
                name="city"
                type="text"
                className={`form-control ${
                  touched.city && errors.city ? "is-invalid" : ""
                }`}
                placeholder="Enter your city"
                value={values.city}
              />
              <ErrorMessage
                name="city"
                component="div"
                className="invalid-feedback"
              />
            </Form.Group>

            <Form.Group controlId="formState" className="mb-3">
              <Form.Label>
                <i className="fas fa-map me-2" style={{ color: "#667eea" }}></i>
                State*
              </Form.Label>
              <Field
                name="state"
                type="text"
                className={`form-control ${
                  touched.state && errors.state ? "is-invalid" : ""
                }`}
                placeholder="Enter your state"
                value={values.state}
              />
              <ErrorMessage
                name="state"
                component="div"
                className="invalid-feedback"
              />
            </Form.Group>

            <Form.Group controlId="formCountry" className="mb-3">
              <Form.Label>
                <i
                  className="fas fa-globe me-2"
                  style={{ color: "#667eea" }}
                ></i>
                Country*
              </Form.Label>
              <Field
                name="country"
                type="text"
                className={`form-control ${
                  touched.country && errors.country ? "is-invalid" : ""
                }`}
                placeholder="Enter your country"
                value={values.country}
              />
              <ErrorMessage
                name="country"
                component="div"
                className="invalid-feedback"
              />
            </Form.Group>

            {errors.submit && (
              <div className="alert alert-danger mt-3">{errors.submit}</div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default memo(NewPaymentForm);
